import * as signalR from '@microsoft/signalr';
import { useEffect, useState } from 'react';

import { showToast } from '../components/reusable/toast';
import { getUserData, onLogout, SIGNALRAPIS } from '../constants/global';
import { pageurl } from '../constants/pageurl';

export type typeConnectionStatus = 'connecting' | 'connected' | 're-connecting' | 'closed';

// The token is required, not optional. api.core's NotificationHub is due to be gated
// with [Authorize] (its four unauthenticated Clients.All broadcast methods have already
// been removed), and drop-down.tsx uses this hook to receive SendLogoutNotification --
// the force-logout-on-new-device signal. An untokened connection would keep working
// right up until the hub is gated and then fail silently, taking force-logout with it.
//
// skipNegotiation means SignalR cannot send an Authorization header, so the token rides
// the query string as access_token; api.core's JwtBearerEvents.OnMessageReceived reads it
// for the /notificationHub path. Matches agent-location-signalr.ts.
const getConnection = (url: string) =>
	new signalR.HubConnectionBuilder()
		.withUrl(url, {
			skipNegotiation: true,
			transport: signalR.HttpTransportType.WebSockets,
			accessTokenFactory: () => getUserData().token || '',
		})
		.configureLogging(signalR.LogLevel.Warning)
		.withAutomaticReconnect()
		.build();

export interface EmailResultRequestSignalR {
	UserId: string;
	Title: string;
	Body: string;
	DownloadLink: string;
}
export interface ResultNotificationSignalR {
	id: number;
	title: string;
	body: string;
	timeStamp: string;
	partyId: number;
}

export interface LogoutRequestSignalR {
	userId: number;
	email: string;
}

export const useSignalR = <T = undefined>(
	url: SIGNALRAPIS
): {
	connectionStatus: typeConnectionStatus;
	notification: T | undefined;
} => {
	const [notification, setNotification] = useState<T>();
	const [connection, setConnection] = useState<signalR.HubConnection>();
	const [connectionStatus, setConnectionStatus] = useState<typeConnectionStatus>('closed');

	const startConnection = () => {
		setConnectionStatus('connecting');
		const connection = getConnection(url);
		connection
			?.start()
			.then(() => {
				setConnectionStatus('connected');
			})
			.catch((error) => {
				console.error('SignalR connection error', error);
				setConnectionStatus('closed');
			});
		setConnection(connection);
	};

	useEffect(() => {
		connection?.on('SendNotification', (data: T) => {
			setNotification(data);

			if (url === SIGNALRAPIS.BROADCAST) {
				if (data) {
					const notification = (typeof data === 'object' && 'body' in data ? data.body : '') as string;

					if (notification) {
						showToast('success', notification);
					}
				}
			}

			if (url === SIGNALRAPIS.AGENTSTATS) {
				const { body } = data as { body: string };
				showToast('success', body);
			}
		});

		connection?.on('SendVolunteerNotification', (data: any) => {
			setNotification(data);
		});

		connection?.on('SendLogoutNotification', (data: any) => {
			if (getUserData().user?.UserId === data.userId.toString()) {
				const message =
					'You have been logged out due to a login attempt on a new device. Please log in again if you wish to continue.';
				localStorage.setItem('device-login', JSON.stringify({ message, timeStamp: new Date().toISOString() }));
				onLogout();
				window.open(pageurl.LOGIN, '_self');
			}
		});

		connection?.onreconnecting(() => {
			setConnectionStatus('re-connecting');
		});
		connection?.onreconnected(() => {
			setConnectionStatus('connected');
		});
		connection?.onclose(() => {
			setConnectionStatus('closed');
		});
	}, [connection]);

	useEffect(() => {
		startConnection();
	}, []);

	return {
		connectionStatus,
		notification,
	};
};
